import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { retrieveCheckoutSession } from '../middleware/registration_service/getRequests';
import { CheckoutSessionResponse } from '../types/stripePayments';
import { useAuth0 } from '@auth0/auth0-react';

const PaymentReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided.');
      setLoading(false);
      return;
    }

    const fetchSessionStatus = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const response = await retrieveCheckoutSession(sessionId, token);
        const session: CheckoutSessionResponse = response?.data;

        setStatus(session?.status ?? session?.paymentStatus ?? null);
        setCustomerEmail(
          session?.customerEmail ?? session?.customerDetails?.email ?? null
        );
      } catch (err: any) {
        console.error('Failed to fetch checkout session status:', err);
        setError(
          err?.response?.data?.message ??
            err?.message ??
            'Failed to verify payment status. Please contact support.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSessionStatus();
  }, [sessionId]);

  const isSuccess = status === 'complete' || status === 'paid';

  return (
    <Container maxWidth="sm" sx={{ mt: 15 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
        }}
      >
        {/* Loading state */}
        {loading && (
          <Box>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Verifying your payment…
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {!loading && error && (
          <Box>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate('/account-settings')}
            >
              Back to Account Settings
            </Button>
          </Box>
        )}

        {/* Success state */}
        {!loading && !error && isSuccess && (
          <Box>
            <CheckCircleIcon
              sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
            />
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Thank you for your subscription. Your payment has been processed
              successfully.
            </Typography>

            {customerEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A confirmation email will be sent to{' '}
                <strong>{customerEmail}</strong>.
              </Typography>
            )}

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 2,
              }}
            >
              <ReceiptLongOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Session ID: {sessionId}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/subscription-details')}
              >
                View Subscription
              </Button>
            </Box>
          </Box>
        )}

        {/* Non-success / pending state */}
        {!loading && !error && !isSuccess && status && (
          <Box>
            <ErrorIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Payment Not Completed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your payment status is: <strong>{status}</strong>. If you believe
              this is an error, please try again or contact support.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/account-settings')}
              >
                Try Again
              </Button>
              <Button variant="outlined" onClick={() => navigate('/')}>
                Go Home
              </Button>
            </Box>
          </Box>
        )}

        {/* No session ID */}
        {!loading && !error && !status && (
          <Box>
            <ErrorIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Invalid Session
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              No valid checkout session was found. Please start a new payment.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/')}>
              Go Home
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default PaymentReturn;
